'use client'
import React, { useEffect, useMemo, useState, useContext } from 'react'
import { Dialog } from '@headlessui/react'
import AppContext from '../../context/AppContext'
import { getElements } from '../../pages/workflow/constants'
import { toast } from "../../utils/toast";
import translations from '../common/Translations'
import CommonButton from '../common/CommonButton'
import ReactSelector from '../common/CommonReactSelector'
import FileChooserPreview from '../common/FileChooserPreview'
import { isRTL } from '../../utils/rtl'

/** Field types offered for a swap, grouped the same way the dropdown lists them. */
const FIELD_TYPE_GROUPS = [
    { labelKey: 'wfFieldGroupBasic', fieldTypes: ['text', 'email', 'number', 'textarea'] },
    { labelKey: 'wfFieldGroupSelection', fieldTypes: ['radio', 'checkbox', 'select'] },
    { labelKey: 'wfFieldGroupMedia', fieldTypes: ['file', 'date', 'time', 'scan', 'ocr', 'gps', 'signal_quality'] },
];

const groupHeadingStyles = (base) => ({
    ...base,
    fontSize: '11px',
    fontWeight: 600,
    letterSpacing: '0.04em',
    paddingTop: '6px',
    paddingBottom: '4px',
});

/** Preview row shows actual controls without stretching radios/checkboxes via text-field styling */
function ModalCurrentFieldPreview({ preview, langMode }) {
    const t = translations[langMode];
    const opts = (preview.allInputValues || [])
        .map((x) => (typeof x === 'string' ? x : x?.inputValue))
        .filter((v) => v !== undefined && v !== null && String(v).trim() !== '');

    const textLikeClasses =
        'mt-3 w-full rounded-md border border-gray-300 p-2 text-sm text-gray-800 bg-gray-50';

    switch (preview.fieldType) {
        case 'textarea':
            return (
                <textarea
                    readOnly
                    disabled
                    rows={3}
                    className={textLikeClasses}
                    value=""
                />
            );
        case 'radio':
            return (
                <div className="mt-3 space-y-2">
                    {opts.length > 0 ? (
                        opts.map((label, index) => (
                            <label
                                key={`${preview.uniqueKey}-radio-${index}`}
                                className="flex items-center gap-2 text-sm text-gray-800"
                            >
                                <input
                                    type="radio"
                                    disabled
                                    name={`modal-preview-radio-${preview.uniqueKey}`}
                                    className="h-4 w-4 shrink-0 border-gray-300 text-blue-600"
                                />
                                <span>{label}</span>
                            </label>
                        ))
                    ) : (
                        <p className="text-sm italic text-gray-500">
                            {t.workflowModalPreviewOptionsEmpty}
                        </p>
                    )}
                </div>
            );
        case 'checkbox':
            return (
                <div className="mt-3 space-y-2">
                    {opts.length > 0 ? (
                        opts.map((label, index) => (
                            <label
                                key={`${preview.uniqueKey}-cb-${index}`}
                                className="flex items-center gap-2 text-sm text-gray-800"
                            >
                                <input
                                    type="checkbox"
                                    disabled
                                    className="h-4 w-4 shrink-0 rounded border-gray-300 text-blue-600"
                                />
                                <span>{label}</span>
                            </label>
                        ))
                    ) : (
                        <p className="text-sm italic text-gray-500">
                            {t.workflowModalPreviewOptionsEmpty}
                        </p>
                    )}
                </div>
            );
        case 'select':
            return (
                <select disabled className={`${textLikeClasses} bg-white`}>
                    <option value="">
                        {opts.length > 0
                            ? t.selectoption
                            : t.workflowModalPreviewOptionsEmpty}
                    </option>
                    {opts.map((label, index) => (
                        <option key={`${preview.uniqueKey}-sel-${index}`} value={label}>
                            {label}
                        </option>
                    ))}
                </select>
            );
        case 'file':
        case 'multi_image':
            return (
                <div className="mt-3">
                    <FileChooserPreview
                        disabled
                        multiple={!!preview.multiple}
                        accept={preview.accept || undefined}
                        size="sm"
                        chooseFileText={t.chooseFile ?? "Choose File"}
                        fileName={t.noFile ?? "No file chosen"}
                    />
                </div>
            );
        case 'date':
            return <input type="date" disabled className={textLikeClasses} />;
        case 'time':
            return <input type="time" disabled className={textLikeClasses} />;
        case 'number':
            return (
                <input
                    type="number" 
                    disabled
                    readOnly
                    className={textLikeClasses}
                    placeholder=""
                    value=""
                />
            );
        case 'email':
            return (
                <input
                    type="email"
                    disabled
                    readOnly
                    className={textLikeClasses}
                    placeholder=""
                    value=""
                />
            );
        case 'scan':
            return (
                <input
                    type="text"
                    disabled
                    readOnly
                    className={textLikeClasses}
                    placeholder={t.Barcode}
                    value=""
                />
            );
        case 'ocr':
        case 'gps':
        case 'signal_quality':
            return (
                <div className="mt-3 rounded-md border border-dashed border-gray-300 bg-gray-50 px-3 py-4 text-sm text-gray-600">
                    {String(preview.fieldType || '').replace(/_/g, ' ').toUpperCase()}
                </div>
            );
        default:
            return (
                <input
                    type="text"
                    disabled
                    readOnly
                    className={textLikeClasses}
                    placeholder=""
                    value=""
                />
            );
    }
}

export default function FieldModal({ open, setOpen, preview, setPreview, droppedItems, setDroppedItems, dragItems, setDragItems }) {
    const [selectedField, setSelectedField] = useState(null);
    const { setNewfield, langMode } = useContext(AppContext);
    const elements = useMemo(() => getElements(langMode), [langMode]);

    const fieldOptions = useMemo(
        () => FIELD_TYPE_GROUPS
            .map((group) => ({
                label: translations[langMode][group.labelKey],
                options: elements
                    .filter((el) => group.fieldTypes.includes(el.fieldType))
                    .map((el) => ({
                        value: el.fieldName,
                        label: el.label || el.fieldName,
                        // Falls back to the label inside the selector when an element has none.
                        tooltip: el.tooltip,
                        field: el,
                    })),
            }))
            .filter((group) => group.options.length > 0),
        [elements, langMode]
    );

    const selectedOption = selectedField
        ? {
            value: selectedField.fieldName,
            label: selectedField.label || selectedField.fieldName,
            field: selectedField,
        }
        : null;

    useEffect(() => {
        if (open) setSelectedField(null);
    }, [open, preview?.uniqueKey]);

    const handleSave = () => {
        if (!selectedField) return;
        if (preview.fieldType === selectedField.fieldType && preview.cloneName === selectedField.cloneName) {
            toast.info(translations[langMode].nochangwsmadetothe);
            return;
        }

        const patch = {
            fieldName: selectedField.fieldName,
            fieldType: selectedField.fieldType,
            multiple: !!selectedField.multiple,
            accept: selectedField.accept || preview.accept,
            cloneName: selectedField.cloneName,
        };

        setDroppedItems((prev) =>
            prev.map((item) =>
                item.uniqueKey === preview.uniqueKey ? { ...item, ...patch } : item
            )
        );
        setDragItems((prev) =>
            prev.map((item) =>
                item.uniqueKey === preview.uniqueKey ? { ...item, ...patch } : item
            )
        );

        const updatedPreview = {
            ...preview,
            ...patch,
        };
        setNewfield(updatedPreview);
        setPreview(updatedPreview);
        setOpen(false);
        setSelectedField(null);
    };

    return (
        <Dialog open={open} onClose={() => setOpen(false)} className="relative z-50">
            <div className="fixed inset-0 bg-black bg-opacity-25" aria-hidden="true" />
            <div className="fixed inset-0 z-50 flex items-center justify-center">
                <Dialog.Panel
                    dir={isRTL(langMode) ? "rtl" : "ltr"}
                    className="bg-white p-6 rounded shadow-xl w-full max-w-md mx-auto"
                >
                    <Dialog.Title className="text-lg font-bold">{translations[langMode].updateinputype}</Dialog.Title>

                    <div className="flex flex-col">
                        <label className='text-gray-700 font-semibold mt-4'>
                            {preview.fieldName}
                        </label>
                        <ModalCurrentFieldPreview preview={preview} langMode={langMode} />
                    </div>

                    <div className="flex flex-col mt-4">
                        <h4 className="text-sm font-medium mb-1">{translations[langMode].selectinputtype}</h4>
                        <ReactSelector
                            dataTestId="fieldDropdown"
                            options={fieldOptions}
                            value={selectedOption}
                            onChange={(option) => setSelectedField(option?.field ?? null)}
                            placeholder={translations[langMode].selectfield}
                            isClearable
                            height="40px"
                            borderRadius="6px"
                            fontSize="14px"
                            maxMenuHeight="200px"
                            styles={{ groupHeading: groupHeadingStyles }}
                        />

                        {selectedField && (
                            <>
                                <div className="mt-4 border p-3 rounded bg-gray-50">
                                    <p className="text-sm font-semibold mb-2">{translations[langMode].selectefieldpreiview}</p>
                                    <p className="text-sm text-gray-700">{translations[langMode].fieldname}: {selectedField.fieldName}</p>
                                    <p className="text-sm text-gray-700">{translations[langMode].fieldtype}: {translations[langMode]?.[`wfType_${selectedField.fieldType}`] ?? selectedField.fieldType}</p>
                                </div>

                            </>
                        )}
                    </div>

                    <div className="mt-6 flex justify-end items-center gap-3">
                        <CommonButton colorIntent="danger"
                            onClick={() => setOpen(false)}
                            variant="danger"
                        >
                            {translations[langMode].cancle}
                        </CommonButton>
                        <CommonButton colorIntent="success"
                            onClick={handleSave}
                            variant="primary"
                            disabled={!selectedField}
                        >
                            {translations[langMode].save}
                        </CommonButton>
                    </div>
                </Dialog.Panel>
            </div>
        </Dialog>
    );
}
